<template>
    <template v-if="model">
        <div class="modal-backdrop fade show"></div>
        <div
            class="modal fade show overflow-inherit-y"
            :class="[props.className]"
            style="display: block;"
            tabindex="-1"
            role="dialog"
            aria-modal="true"
            :aria-label="props.title"
            :id="computedId"
            ref="modalEl"
            @keydown.enter.prevent="handleEnter"
            @keydown.escape.prevent="hideModal"
            @keydown.tab.prevent="handleTab"
            @click.self.prevent>
            <div
                class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                :style="`max-width: ${props.width}`">
                <div
                    class="modal-content"
                    style="overflow: visible">
                    <div class="modal-header">
                        <h4 class="modal-title">{{ props.title }}</h4>
                        <button
                            type="button"
                            class="btn-close"
                            aria-label="Close"
                            @click.prevent="hideModal"></button>
                    </div>
                    <div
                        class="modal-body"
                        :class="modalBodyContainerClass">
                        <p>
                            <slot
                                name="body"
                                :on-dismiss="hideModal"
                                :on-submit="handleSubmit"
                                >{{ props.body }}</slot
                            >
                            <slot></slot>
                        </p>
                    </div>
                    <div
                        class="modal-footer"
                        v-if="props.showFooter">
                        <slot
                            name="footer"
                            :on-dismiss="hideModal"
                            :on-submit="handleSubmit"></slot>
                    </div>
                </div>
            </div>
        </div>
    </template>
</template>

<script setup lang="ts">
import { useId, watch, ref, nextTick } from "vue";

const computedId = useId();
const model = defineModel({ type: Boolean });
const modalEl = ref<HTMLElement | null>(null);
const triggerElement = ref<HTMLElement | null>(null);
const props = withDefaults(
    defineProps<{
        title?: string;
        body?: string;
        showFooter?: boolean;
        className?: string;
        width?: string;
        modalBodyContainerClass?: string;
    }>(),
    {
        title: "",
        width: "750px",
        modalBodyContainerClass: "",
    }
);

const emit = defineEmits<{
    (event: "dismiss"): void;
    (event: "submit"): void;
}>();

function handleSubmit() {
    model.value = false;
    emit("submit");
}

function handleEnter() {
    handleSubmit();
}

function showModal() {
    model.value = true;
}

function hideModal() {
    model.value = false;
    emit("dismiss");
}

const FOCUSABLE_SELECTORS = [
    "a[href]",
    "area[href]",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "button:not([disabled])",
    "iframe",
    "object",
    "embed",
    "[contenteditable]",
    "[tabindex]:not([tabindex='-1'])",
].join(",");

function getFocusableElements() {
    if (!modalEl.value) {
        return [] as HTMLElement[];
    }

    return Array.from(
        modalEl.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
    ).filter(
        (element) =>
            !element.hasAttribute("disabled") &&
            element.tabIndex !== -1 &&
            element.offsetParent !== null
    );
}

function handleTab(event: KeyboardEvent) {
    const focusable = getFocusableElements();
    const activeElement = document.activeElement as HTMLElement | null;

    if (focusable.length === 0) {
        modalEl.value?.focus();
        return;
    }

    const currentIndex = activeElement
        ? focusable.indexOf(activeElement)
        : -1;
    const lastIndex = focusable.length - 1;

    if (event.shiftKey) {
        if (currentIndex <= 0) {
            focusable[lastIndex].focus();
        } else {
            focusable[currentIndex - 1].focus();
        }
    } else {
        if (currentIndex === -1 || currentIndex === lastIndex) {
            focusable[0].focus();
        } else {
            focusable[currentIndex + 1].focus();
        }
    }
}

// Keep the internal model in sync with the default `v-model` prop (`modelValue`).
watch(
    () => (props as any).modelValue,
    (modelValue) => {
        if (modelValue !== model.value) {
            model.value = modelValue as boolean;
        }
    }
);

// Focus the modal element when it's shown so key events are received,
// and return focus to the triggering element when it's hidden.
watch(model, async (val) => {
    if (val) {
        triggerElement.value = document.activeElement as HTMLElement | null;
        await nextTick();
        modalEl.value?.focus();
    } else {
        if (triggerElement.value?.isConnected) {
            triggerElement.value.focus();
        }
        triggerElement.value = null;
    }
});

defineExpose({ show: showModal, hide: hideModal });
</script>

<style lang="css">
.overflow-inherit-y {
    overflow-y: inherit !important;
}
</style>
